import React from 'react';
import UrgencyIcon from '../../atoms/urgency/urgency';
import './UrgencyPill.scss';
import { urgencyLevels } from '../../../constants';

/**
 * Where the pill sits in the page.
 *
 * `inline` (the default) is the pill itself: a control in normal flow that
 * occupies its own box, so a host can mount it as a field/value editor without
 * knowing anything about positioning.
 *
 * `popover` is the pill PLUS the menu behaviour it was originally written with
 * — absolutely positioned under the trigger that opened it. A host asking for
 * it owns the positioned ancestor (`position: relative`) the pill anchors to
 * and the open/close state that mounts it.
 */
export type UrgencyPillPlacement = 'inline' | 'popover';

interface UrgencyPillMoleculeProps {
  urgencyValue: number;
  onUrgencyChange: (urgency: string) => void;
  /** Stack items vertically instead of the default horizontal pill row */
  vertical?: boolean;
  /** See {@link UrgencyPillPlacement}. Defaults to `inline`. */
  placement?: UrgencyPillPlacement;
}

// The base .urgency-pill has align-items:center which in column flex direction
// centers each item horizontally by its own width — shorter labels produce
// narrower items that land at different X positions. align-items:stretch makes
// every item expand to the full container width so icons always align.
const VERTICAL_PILL_STYLE: React.CSSProperties = {
  alignItems: 'stretch',
};

const VERTICAL_ITEM_STYLE: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  padding: '10px 16px',
  gap: '8px',
};

// Zeroes the 5px left/right margins urgency.scss bakes into every icon class.
const VERTICAL_ICON_STYLE: React.CSSProperties = {
  margin: 0,
  flexShrink: 0,
};

const UrgencyPillMolecule: React.FC<UrgencyPillMoleculeProps> = ({
  urgencyValue,
  onUrgencyChange,
  vertical = false,
  placement = 'inline',
}) => {
  return (
    <div
      className={`urgency-pill urgency-pill--${placement}${vertical ? ' urgency-pill--vertical' : ''}`}
      style={vertical ? VERTICAL_PILL_STYLE : undefined}
    >
      {urgencyLevels.map((level) => (
        <div
          key={level.value}
          className={`urgency-item ${level.value === urgencyValue ? 'urgency-item--selected' : ''}`}
          style={vertical ? VERTICAL_ITEM_STYLE : undefined}
          onClick={(e) => {
            e.stopPropagation();
            onUrgencyChange(level.value.toString());
          }}
        >
          <UrgencyIcon
            urgencyValue={level.value}
            style={vertical ? VERTICAL_ICON_STYLE : undefined}
          />
          <span className="urgency-item__label">{level.label}</span>
        </div>
      ))}
    </div>
  );
};

export default UrgencyPillMolecule;
